import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

let activeReportMsClose: (() => void) | null = null;

const TYPEAHEAD_MIN = 3;

export type ReportMultiSelectOptionItem = { value: string; label: string };

function openReportMultiSelect(close: () => void) {
  if (activeReportMsClose && activeReportMsClose !== close) activeReportMsClose();
  activeReportMsClose = close;
}

function closeReportMultiSelect(close: () => void) {
  if (activeReportMsClose === close) activeReportMsClose = null;
}

export function ReportMultiSelect({
  label,
  value,
  options = [],
  optionItems,
  onChange,
  placeholder,
  disabled = false,
  single = false,
  searchable = true,
  showCheckboxes = true,
  allOption,
}: {
  label: string;
  value: string[];
  options?: readonly string[];
  /** When set, `value` holds item values (e.g. ids) and labels are shown in the UI. */
  optionItems?: readonly ReportMultiSelectOptionItem[];
  placeholder: string;
  onChange: (v: string[]) => void;
  disabled?: boolean;
  /** When true, only one option may be selected (data dictionary: User Name). */
  single?: boolean;
  /** When false, hides the typeahead search box (e.g. Export as with two options). */
  searchable?: boolean;
  /** When false, renders a plain list without checkboxes (e.g. Export as dropdown). */
  showCheckboxes?: boolean;
  /** When set, prepends an "All" row; empty value means All (no filter). String options only. */
  allOption?: string;
}) {
  const { t } = useTranslation();
  const instanceId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const items = useMemo((): ReportMultiSelectOptionItem[] => {
    if (optionItems?.length) return [...optionItems];
    const base = allOption ? options.filter((o) => o !== allOption) : [...options];
    const mapped = base.map((o) => ({ value: o, label: o }));
    if (allOption) return [{ value: allOption, label: allOption }, ...mapped];
    return mapped;
  }, [allOption, optionItems, options]);

  const labelByValue = useMemo(
    () => new Map(items.map((item) => [item.value, item.label])),
    [items],
  );

  const displayLabel = (itemValue: string) => labelByValue.get(itemValue) ?? itemValue;

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < TYPEAHEAD_MIN) return items;
    return items.filter((item) => item.label.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest?.('.ms-wrap')) {
        close();
        closeReportMultiSelect(close);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open, close]);

  useEffect(() => {
    if (disabled && open) {
      close();
      closeReportMultiSelect(close);
    }
  }, [disabled, open, close]);

  useEffect(() => {
    return () => closeReportMultiSelect(close);
  }, [close]);

  const removeTag = (item: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((x) => x !== item));
  };

  const clearAll = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onChange([]);
    close();
    closeReportMultiSelect(close);
  };

  const toggleOpen = () => {
    if (disabled) return;
    if (open) {
      close();
      closeReportMultiSelect(close);
      return;
    }
    openReportMultiSelect(close);
    setOpen(true);
  };

  const handleCheck = (itemValue: string, checked: boolean) => {
    if (allOption && !optionItems && itemValue === allOption) {
      if (checked) onChange([]);
      return;
    }
    if (single) {
      if (checked) onChange([itemValue]);
      return;
    }
    const next = checked ? [...value, itemValue] : value.filter((x) => x !== itemValue);
    onChange(next);
  };

  const selectOption = (itemValue: string) => {
    if (allOption && !optionItems && itemValue === allOption) {
      onChange([]);
      close();
      closeReportMultiSelect(close);
      return;
    }
    if (single) {
      onChange([itemValue]);
    } else if (value.includes(itemValue)) {
      onChange(value.filter((x) => x !== itemValue));
    } else {
      onChange([...value, itemValue]);
    }
    close();
    closeReportMultiSelect(close);
  };

  const isAllSelected = Boolean(allOption) && !optionItems && value.length === 0;
  const buttonLabel = isAllSelected ? allOption : null;
  const showClear = !disabled && value.length > 0;

  const plainSingle = single && !showCheckboxes;

  return (
    <div className={`flt-f ms-wrap${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}${showClear ? ' has-clear' : ''}`} data-ms-id={instanceId}>
      <div className="flt-l">{label}</div>
      <div className="ms-field">
      <button
        className={`ms-btn${plainSingle ? '' : ' ms-btn--tags'}`}
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
      >
        {plainSingle ? (
          <span className="ms-btn-value">
            {value.length === 0 ? (
              <span className="ms-btn-placeholder">{placeholder}</span>
            ) : (
              displayLabel(value[0])
            )}
          </span>
        ) : (
          <span className="ms-btn-tags">
            {value.length === 0 ? (
              buttonLabel ? (
                <span className="ms-tag">
                  <span className="ms-tag-text tip tip-start" data-tip={buttonLabel} title={buttonLabel}>
                    {buttonLabel}
                  </span>
                </span>
              ) : (
                <span className="ms-btn-placeholder">{placeholder}</span>
              )
            ) : (
              value.map((item) => {
                const tagLabel = displayLabel(item);
                return (
                  <span key={item} className="ms-tag">
                    <span className="ms-tag-text tip tip-start" data-tip={tagLabel} title={tagLabel}>
                      {tagLabel}
                    </span>
                    {!single ? (
                      <button
                        type="button"
                        className="ms-tag-x"
                        aria-label={`Remove ${tagLabel}`}
                        onClick={(e) => removeTag(item, e)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    ) : null}
                  </span>
                );
              })
            )}
          </span>
        )}
        <svg
          className="ms-btn-chev"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {showClear ? (
        <button
          type="button"
          className="ms-btn-clear"
          aria-label={t('reports.filters.clearSelection')}
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={clearAll}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      ) : null}
      </div>
      {open ? (
        <div className="ms-dd" role="listbox" aria-label={label}>
          {searchable ? (
            <input
              className="ms-dd-search"
              type="text"
              value={query}
              placeholder={t('reports.filters.typeToSearch')}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setQuery(e.target.value)}
            />
          ) : null}
          {visibleItems.length === 0 ? (
            <div className="ms-dd-empty">{t('common.noResultsFound')}</div>
          ) : showCheckboxes ? (
            visibleItems.map((item) => {
              const checked =
                allOption && !optionItems && item.value === allOption
                  ? isAllSelected
                  : value.includes(item.value);
              return (
                <label key={item.value} className="ms-opt">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => handleCheck(item.value, e.target.checked)}
                  />
                  <span className="ms-opt-label tip tip-start" data-tip={item.label} title={item.label}>
                    {item.label}
                  </span>
                </label>
              );
            })
          ) : (
            visibleItems.map((item) => {
              const selected =
                allOption && !optionItems && item.value === allOption
                  ? isAllSelected
                  : value.includes(item.value);
              return (
                <button
                  key={item.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`ms-opt ms-opt--plain${selected ? ' is-selected' : ''}`}
                  onClick={() => selectOption(item.value)}
                >
                  <span className="ms-opt-label tip tip-start" data-tip={item.label} title={item.label}>
                    {item.label}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
